import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  CircularProgress,
  TableContainer,
  Snackbar,
  Alert,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { useDomain } from "context/DomainContext";
import { Delete, Edit } from "@mui/icons-material";
import dayjs from "dayjs";
import { useUser } from "context/UserContext";
import { useAuth } from "context/AuthContext";
import { useSystemTheme } from "context/SystemThemeContext";
import encryptedStorage from "utils/encryptedStorage";
import { getAccessToken } from "utils/tokenManager";

const Users = () => {
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [role, setRole] = useState("read only");
  const user_role = localStorage.getItem("role");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editUserEmail, setEditUserEmail] = useState("");
  const [editUserRole, setEditUserRole] = useState("");

  const { googleUser } = useUser();
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
     const storedUser = encryptedStorage.getItem('auth_user');
  
  const { mode } = useSystemTheme();
  const accessToken = getAccessToken();
  const isDarkMode = mode === "dark";

  useEffect(() => {
    fetchUsers();
  }, [domainId]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/user/list`, {
        headers: {
          "DOMAIN-TOKEN": domainId,
        },
      });
      if (res.data.result?.length > 0) setUsers(res.data.result);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!email || !isEmailValid || !role) return;
    setSaving(true);
 
    try {
      console.log("Stored User:", storedUser.id);
      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/user/invite`,
        { mail: email, role: role,user_id: storedUser?.id },
        {
          headers: {
            "DOMAIN-TOKEN": domainId,
            "x-api-key": process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${accessToken}`
          },
        }
      );

      const { resultCode, result } = response.data;

      if (resultCode === "0") {
        setSnackbarMessage(result || "Invitation sent successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEmail("");
        setShowForm(false);
        fetchUsers();
      } else {
        setSnackbarMessage("Failed to send invitation.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error adding user", err);
      setSnackbarMessage("Failed to send invitation.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (email) => {
    setSelectedUserEmail(email);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${FAST_API_BASE_URL}/api/invited-users`,
        {
          headers: {
            "DOMAIN-TOKEN": domainId,
          },
          data: {
            admin_email: googleUser?.email || localStorage.getItem("customEmail"),
            target_emails: [selectedUserEmail],
          },
        }
      );

      setSnackbarMessage("User removed successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchUsers();
    } catch (error) {
      console.error("Error removing user:", error);
      setSnackbarMessage("Failed to remove user.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setConfirmOpen(false);
      setSelectedUserEmail(null);
    }
  };

  const handleEditClick = (email, currentRole) => {
    setEditUserEmail(email);
    setEditUserRole(currentRole);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await axios.patch(
        `${FAST_API_BASE_URL}/api/user/update-roles`,
        {
          admin_email: googleUser?.email || localStorage.getItem("customEmail"),
          user_updates: [
            {
              email: editUserEmail,
              new_role: editUserRole,
            },
          ],
        },
        {
          headers: {
            "DOMAIN-TOKEN": domainId,
          },
        }
      );
      setSnackbarMessage("User role updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user role:", error);
      setSnackbarMessage("Failed to update user role.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setEditOpen(false);
      setEditUserEmail("");
      setEditUserRole("");
    }
  };

  const handleDeleteCancel = () => {
    setConfirmOpen(false);
    setSelectedUserEmail(null);
  };

  const validateEmail = (value) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(regex.test(value));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
  };

  return (
    <SoftBox
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#fff",
        p: 4,
        borderRadius: "16px",
        boxShadow: isDarkMode 
          ? "0px 6px 20px rgba(0, 0, 0, 0.3)" 
          : "0px 6px 20px rgba(0, 0, 0, 0.06)",
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
      >
        <SoftTypography variant="h5" fontWeight="bold" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
          Users
        </SoftTypography>

        {user_role == "admin" && (
          <Button
            variant="contained"
            onClick={() => setShowForm(!showForm)}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1a767a",
              },
            }}
          >
            {showForm ? "Cancel" : "Add User"}
          </Button>
        )}
      </SoftBox>

      {showForm && (
        <SoftBox mb={4}>
          <SoftBox display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
            <SoftBox>
              <SoftTypography variant="button" fontWeight="bold" mb={1} display="block" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
                Email Address
              </SoftTypography>
              {!isEmailValid && email.length > 0 && (
                <SoftTypography color="error" fontSize="0.8rem" mt={0.5} sx={{ color: "#f44336" }}>
                  Enter valid email address
                </SoftTypography>
              )}
              <TextField
                variant="outlined"
                placeholder="Enter Email"
                value={email}
                onChange={handleEmailChange}
                sx={{
                  width: "250px",
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? '#2d2d3f' : '#fff',
                    borderRadius: "8px",
                    '& fieldset': {
                      borderColor: email
                        ? isEmailValid
                          ? "green"
                          : "red"
                        : (isDarkMode ? "#4a5568" : "#ccc"),
                    },
                    '&:hover fieldset': {
                      borderColor: email
                        ? isEmailValid
                          ? "green"
                          : "red"
                        : (isDarkMode ? "#4a5568" : "#ccc"),
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: isDarkMode ? '#e2e8f0' : '#344767',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: isDarkMode ? '#a0aec0' : '#9e9e9e',
                    opacity: 1,
                  },
                }}
                InputLabelProps={{ shrink: false }}
              />
            </SoftBox>

            <SoftBox>
              <SoftTypography variant="button" fontWeight="bold" mb={1} display="block" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
                Role
              </SoftTypography>
              <SoftSelect
                defaultValue={{ value: "read only", label: "Read Only" }}
                options={[
                  { value: "admin", label: "Admin" },
                  { value: "read only", label: "Read Only" },
                  { value: "read&write", label: "Read & Write" },
                ]}
                onChange={(option) => setRole(option.value)}
                size="medium"
                sx={{
                  width: "180px",
                  '& .MuiSelect-root': {
                    color: isDarkMode ? '#e2e8f0' : undefined,
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? '#2d2d3f' : '#fff',
                    '& fieldset': {
                      borderColor: isDarkMode ? '#4a5568' : '#d2d6da',
                    },
                  },
                }}
              />
            </SoftBox>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!email || !isEmailValid || saving}
              sx={{
                backgroundColor: "#219297",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                py: 1,
                height: "42px",
                mt: 3,
                "&:hover": {
                  backgroundColor: "#1a767a",
                },
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </SoftBox>
        </SoftBox>
      )}

      <SoftTypography variant="h6" fontWeight="bold" mb={2} sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
        Existing Users
      </SoftTypography>

      {loading ? (
        <SoftBox display="flex" justifyContent="center">
          <CircularProgress size={28} sx={{ color: "#219297" }} />
        </SoftBox>
      ) : (
        <TableContainer sx={{ 
          boxShadow: "none",
          '& .MuiTable-root': {
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
          }
        }}>
          <Table>
            <SoftBox component="thead">
              <TableRow>
                <DataTableHeadCell 
                  sorted="none"
                  sx={{
                    '& .MuiBox-root': {
                      color: isDarkMode ? "#e2e8f0 !important" : undefined,
                    }
                  }}
                >
                  Email
                </DataTableHeadCell>
                <DataTableHeadCell 
                  sorted="none"
                  sx={{
                    '& .MuiBox-root': {
                      color: isDarkMode ? "#e2e8f0 !important" : undefined,
                    }
                  }}
                >
                  Access
                </DataTableHeadCell>
                <DataTableHeadCell 
                  sorted="none"
                  sx={{
                    '& .MuiBox-root': {
                      color: isDarkMode ? "#e2e8f0 !important" : undefined,
                    }
                  }}
                >
                  Added At
                </DataTableHeadCell>
                <DataTableHeadCell 
                  sorted="none"
                  sx={{
                    '& .MuiBox-root': {
                      color: isDarkMode ? "#e2e8f0 !important" : undefined,
                    }
                  }}
                >
                  Status
                </DataTableHeadCell>
                <DataTableHeadCell 
                  sorted="none"
                  sx={{
                    '& .MuiBox-root': {
                      color: isDarkMode ? "#e2e8f0 !important" : undefined,
                    }
                  }}
                >
                  Actions
                </DataTableHeadCell>
              </TableRow>
            </SoftBox>
            <TableBody>
              {users.length > 0 ? (
                users.map((user) => (
                  <TableRow 
                    key={user.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: isDarkMode ? '#0d1425' : '#f5f5f5',
                      },
                    }}
                  >
                    <DataTableBodyCell 
                      sx={{
                        color: isDarkMode ? "#e2e8f0 !important" : undefined,
                        borderBottomColor: isDarkMode ? "#1a2542 !important" : undefined,
                      }}
                    >
                      {user.mail}
                    </DataTableBodyCell>
                    <DataTableBodyCell 
                      sx={{
                        color: isDarkMode ? "#e2e8f0 !important" : undefined,
                        borderBottomColor: isDarkMode ? "#1a2542 !important" : undefined,
                      }}
                    >
                      {user.role === "read only"
                        ? "Read Only"
                        : user.role === "read&write"
                        ? "Read and Write"
                        : user.role}
                    </DataTableBodyCell>
                    <DataTableBodyCell 
                      sx={{
                        color: isDarkMode ? "#e2e8f0 !important" : undefined,
                        borderBottomColor: isDarkMode ? "#1a2542 !important" : undefined,
                      }}
                    >
                      {dayjs(user.created_at).format("MMM D, YYYY hh:mm A")}
                    </DataTableBodyCell>
                    <DataTableBodyCell 
                      sx={{
                        color: isDarkMode ? "#e2e8f0 !important" : undefined,
                        borderBottomColor: isDarkMode ? "#1a2542 !important" : undefined,
                      }}
                    >
                      {user.is_invited === 1 ? "Invited" : "Active User"}
                    </DataTableBodyCell>
                    <DataTableBodyCell 
                      sx={{
                        borderBottomColor: isDarkMode ? "#1a2542 !important" : undefined,
                      }}
                    >
                      <SoftBox display="flex" gap={1}>
                        {user.role !== "admin" && (
                          <Delete
                            onClick={() => handleDeleteClick(user.mail)}
                            style={{ cursor: "pointer", color: "#f44336", height: 20, width: 20 }}
                          />
                        )}
                        
                        {user.role !== "admin" && user_role === "admin" && (
                          <Tooltip
                            title={
                              user.is_invited === 1
                                ? "Invited user's role can't be changed"
                                : "Edit user role"
                            }
                            arrow
                          >
                            <Edit
                              onClick={() =>
                                user.is_invited !== 1 && handleEditClick(user.mail, user.role)
                              }
                              style={{
                                cursor: user.is_invited === 1 ? "not-allowed" : "pointer",
                                color: "#219297",
                                height: 20,
                                width: 20,
                                opacity: user.is_invited === 1 ? 0.5 : 1,
                                pointerEvents: user.is_invited === 1 ? "none" : "auto",
                              }}
                            />
                          </Tooltip>
                        )}
                      </SoftBox>
                    </DataTableBodyCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <DataTableBodyCell colSpan={5} align="center">
                    No users added yet.
                  </DataTableBodyCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={confirmOpen} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
            borderRadius: "16px",
          }
        }}
      >
        <DialogTitle sx={{ color: isDarkMode ? "#e2e8f0" : undefined, fontWeight: 600 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <SoftTypography sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
            Do you want to remove <strong>{selectedUserEmail}</strong> from this site?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} sx={{ color: isDarkMode ? "#a0aec0" : "inherit" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1a767a",
              },
            }}
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={editOpen} 
        onClose={() => setEditOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
            borderRadius: "16px",
          }
        }}
      >
        <DialogTitle sx={{ color: isDarkMode ? "#e2e8f0" : undefined, fontWeight: 600 }}>
          Edit User Role
        </DialogTitle>
        <DialogContent>
          <SoftBox mb={2}>
            <SoftTypography variant="body2" fontWeight="bold" sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
              Email:
            </SoftTypography>
            <SoftTypography sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
              {editUserEmail}
            </SoftTypography>
          </SoftBox>
          <SoftBox>
            <SoftTypography variant="body2" fontWeight="bold" mb={1} sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
              Select New Role:
            </SoftTypography>
            <SoftSelect
              defaultValue={{
                value: editUserRole,
                label: editUserRole === "read&write"
                  ? "Read & Write"
                  : editUserRole === "read only"
                  ? "Read Only"
                  : "Admin",
              }}
              options={[
                { value: "admin", label: "Admin" },
                { value: "read only", label: "Read Only" },
                { value: "read&write", label: "Read & Write" },
              ]}
              onChange={(option) => setEditUserRole(option.value)}
              size="medium"
              sx={{
                width: "100%",
                '& .MuiSelect-root': {
                  color: isDarkMode ? '#e2e8f0' : undefined,
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDarkMode ? '#2d2d3f' : '#fff',
                  '& fieldset': {
                    borderColor: isDarkMode ? '#4a5568' : '#d2d6da',
                  },
                },
              }}
            />
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} sx={{ color: isDarkMode ? "#a0aec0" : "inherit" }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            sx={{
              backgroundColor: "#219297",
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#1a767a",
              },
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
};

export default Users;